import React from 'react';
import { connect } from 'react-redux';
import { translate } from '../../../base/i18n/functions';
import { getLocalizedCaptionToneHint } from '../../captionLocalization';
import { getLocalParticipant } from '../../../base/participants/functions';
import { getLargeVideoParticipant } from '../../../large-video/functions';
import { isLayoutTileView } from '../../../video-layout/functions.web';
import { AbstractCaptions, _abstractMapStateToProps } from '../AbstractCaptions';
import { getActiveCaptionSurface, getCaptionSizeClassName } from '../../captionPreferences';
import { getCaptionTonePresentation } from '../../captionTone';
/**
 * React {@code Component} which can display speech-to-text results from
 * Jigasi as subtitles.
 *
 * Phone captions (the interpreter's phone call, interpreter-phone/
 * phoneCaptions.ts) go in their own panel beside the video, never over it,
 * and under the video on a narrow screen (css/_transcription-subtitles.scss;
 * captions-web.html frame 3, 2026-10-29). Room captions keep their overlay.
 */
export class Captions extends AbstractCaptions {
    /**
     * Implements React's {@link Component#render()}: room captions in their
     * overlay, phone captions in their panel.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { _displaySubtitles, _requestingSubtitles, _transcripts } = this.props;
        if (!_requestingSubtitles || !_displaySubtitles || !_transcripts || !_transcripts.size) {
            return null;
        }
        const room = [];
        const phone = [];
        _transcripts.forEach((transcript, id) => {
            if (transcript.phone) {
                phone.push([id, transcript]);
            }
            else {
                room.push(this._renderParagraph(id, transcript));
            }
        });
        return (React.createElement(React.Fragment, null,
            room.length ? this._renderSubtitlesContainer(room) : null,
            phone.length ? this._renderPhoneCaptions(phone) : null));
    }
    /**
     * The phone captions panel: "Phone captions" with a Live mark, then each
     * line under its speaker. Older lines dim and the newest is bright (the
     * stylesheet's last-child rule).
     *
     * @param {Array} lines - The phone caption lines, oldest first.
     * @protected
     * @returns {ReactElement}
     */
    _renderPhoneCaptions(lines) {
        const { _captionPreferences, t } = this.props;
        return (React.createElement("section", { "aria-label": t('vrs.phoneCaptions.title'), className: `phone-captions-panel ${getCaptionSizeClassName(_captionPreferences.captionSize)}` },
            React.createElement("div", { className: 'phone-captions-header' },
                React.createElement("span", { className: 'phone-captions-title' }, t('vrs.phoneCaptions.title')),
                React.createElement("span", { className: 'phone-captions-live' },
                    React.createElement("span", { "aria-hidden": 'true', className: 'phone-captions-dot' }),
                    t('vrs.phoneCaptions.live'))),
            React.createElement("ol", { "aria-live": 'polite', className: 'phone-captions-lines' }, lines.map(([id, line]) => (React.createElement("li", { className: `phone-captions-line${line.interim ? ' interim' : ''}`, key: id },
                React.createElement("span", { className: 'phone-captions-speaker' }, line.speaker),
                React.createElement("span", { className: 'phone-captions-text', dir: 'auto' }, line.body ?? line.text)))))));
    }
    /**
     * Renders the transcription text.
     *
     * @param {string} id - The ID of the transcript message from which the
     * {@code text} has been created.
     * @param {Object} transcript - Subtitles text and optional tone metadata.
     * @protected
     * @returns {ReactElement} - The React element which displays the text.
     */
    _renderParagraph(id, transcript) {
        const { _captionPreferences } = this.props;
        const presentation = getCaptionTonePresentation(transcript.tone, {
            mode: _captionPreferences.expressiveMode,
            motionEnabled: _captionPreferences.toneMotion,
            surface: getActiveCaptionSurface('malkaVrsCc'),
            toneMarkerLane: _captionPreferences.expressiveMode !== 'minimal'
                && _captionPreferences.toneMarkerLane
        });
        const showMarkerLane = Boolean(presentation.symbols.length);
        const hint = getLocalizedCaptionToneHint(presentation, this.props.t);
        return (React.createElement("p", { key: id },
            showMarkerLane ? (React.createElement("span", { "aria-hidden": 'true', className: 'caption-tone__marker-lane' }, presentation.symbols.join(' '))) : null,
            _captionPreferences.screenReaderToneHints && hint ? (React.createElement("span", { className: 'caption-tone__screen-reader-hint' }, hint)) : null,
            React.createElement("span", { className: presentation.className, dir: 'auto', style: presentation.style }, transcript.text)));
    }
    /**
     * Renders the subtitles container.
     *
     * @param {Array<ReactElement>} paragraphs - An array of elements created
     * for each subtitle using the {@link _renderParagraph} method.
     * @protected
     * @returns {ReactElement} - The subtitles container.
     */
    _renderSubtitlesContainer(paragraphs) {
        const { _captionPreferences } = this.props;
        const presentation = getCaptionTonePresentation(undefined, {
            mode: _captionPreferences.expressiveMode,
            motionEnabled: _captionPreferences.toneMotion,
            surface: getActiveCaptionSurface('malkaVrsCc')
        });
        const className = this.props._isLifted
            ? 'transcription-subtitles lifted'
            : 'transcription-subtitles';
        return (React.createElement("div", { className: `${className} ${presentation.modeClassName} ${getCaptionSizeClassName(_captionPreferences.captionSize)} ${presentation.motionClassName}` }, paragraphs));
    }
}
/**
 * Maps (parts of) the redux state to the associated {@code }'s
 * props.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {Object}
 */
function mapStateToProps(state, ownProps) {
    const isTileView = isLayoutTileView(state);
    const largeVideoParticipant = getLargeVideoParticipant(state);
    const localParticipant = getLocalParticipant(state);
    return {
        ..._abstractMapStateToProps(state, ownProps),
        _isLifted: Boolean(largeVideoParticipant && largeVideoParticipant?.id !== localParticipant?.id && !isTileView)
    };
}
export default translate(connect(mapStateToProps)(Captions));
